import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Mail, Bell, User, Shield, ArrowRight, Trash2, AlertTriangle } from "lucide-react";
import { Link } from "wouter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface User {
  id: string;
  email: string;
  name?: string;
  notificationEmail?: string;
  emailNotifications: boolean;
  plan: string;
  scanCredits: number;
}

export default function Settings() {
  const { toast } = useToast();
  const [notificationEmail, setNotificationEmail] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [alertFrequency, setAlertFrequency] = useState("immediate");
  const [alertType, setAlertType] = useState("all");
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  const { data: user, isLoading } = useQuery<User>({
    queryKey: ["/api/user"],
  });

  // Update local state when user data loads
  React.useEffect(() => {
    if (user) {
      // Default to user's email if notificationEmail is not set
      setNotificationEmail(user.notificationEmail || user.email || "");
      setEmailNotifications(user.emailNotifications);
      
      // Load alert preferences if available
      const preferences = (user as any)?.preferences;
      if (preferences) {
        if (typeof preferences === 'string') {
          try {
            const parsed = JSON.parse(preferences);
            if (parsed.alertFrequency) setAlertFrequency(parsed.alertFrequency);
            if (parsed.alertType) setAlertType(parsed.alertType);
          } catch (e) {
            // Ignore parse errors
          }
        } else {
          if (preferences.alertFrequency) setAlertFrequency(preferences.alertFrequency);
          if (preferences.alertType) setAlertType(preferences.alertType);
        }
      }
    }
  }, [user]);

  const cleanupMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("/api/scans/cleanup/all", {
        method: "DELETE",
      });
      return response;
    },
    onSuccess: (data) => {
      toast({
        title: "Cleanup Successful",
        description: `Deleted ${data.deleted?.scansDeleted || 0} scans, ${data.deleted?.alertsDeleted || 0} alerts, and all related findings.`,
      });
      setShowCleanupDialog(false);
      setIsCleaningUp(false);
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["/api/scans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/alerts"] });
    },
    onError: (error: any) => {
      toast({
        title: "Cleanup Failed",
        description: error.message || "Failed to delete scans and findings. Please try again.",
        variant: "destructive",
      });
      setIsCleaningUp(false);
    },
  });

  const handleCleanup = async () => {
    setIsCleaningUp(true);
    cleanupMutation.mutate();
  };

  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: { 
      notificationEmail?: string; 
      emailNotifications?: boolean;
      alertFrequency?: string;
      alertType?: string;
    }) => {
      return apiRequest("/api/user/notification-settings", "PATCH", settings);
    },
    onSuccess: () => {
      toast({
        title: "Settings Updated",
        description: "Your notification preferences have been saved successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    },
    onError: () => {
      toast({
        title: "Update Failed",
        description: "There was an error updating your settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate({
      notificationEmail,
      emailNotifications,
      alertFrequency,
      alertType,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Account Settings</h1>
          <p className="text-muted-foreground">
            Manage your account preferences and notification settings.
          </p>
        </div>
        <div className="space-y-4">
          <div className="h-32 bg-muted rounded animate-pulse" />
          <div className="h-32 bg-muted rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Account Settings</h1>
        <p className="text-muted-foreground">
          Manage your account preferences and notification settings.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Account Information
            </CardTitle>
            <CardDescription>
              Your basic account information and subscription details.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Account Email</Label>
                <Input
                  id="email"
                  value={user?.email || ""}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div>
                <Label htmlFor="plan">Current Plan</Label>
                <Input
                  id="plan"
                  value={user?.plan?.toUpperCase() || "FREE"}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                value={user?.name || ""}
                placeholder="Enter your display name"
                disabled
                className="bg-muted"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Contact support to update your display name.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Email Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Notifications
            </CardTitle>
            <CardDescription>
              Configure where you receive security alerts and scan reports.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="notification-email">Notification Email Address</Label>
              <Input
                id="notification-email"
                type="email"
                placeholder="Enter email for security alerts"
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                className="max-w-md"
              />
              <p className="text-sm text-muted-foreground">
                Security scan reports and alerts will be sent to this email address.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Email Notifications</Label>
                <div className="text-sm text-muted-foreground">
                  Receive email alerts for security issues and scan results.
                </div>
              </div>
              <Switch
                checked={emailNotifications}
                onCheckedChange={setEmailNotifications}
              />
            </div>

            <div className="pt-4 border-t">
              <Button 
                onClick={handleSaveSettings}
                disabled={updateSettingsMutation.isPending}
                className="w-full sm:w-auto"
              >
                {updateSettingsMutation.isPending ? "Saving..." : "Save Notification Settings"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Security Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Alert Preferences
            </CardTitle>
            <CardDescription>
              Choose what types of security alerts you want to receive and how often.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Alert Frequency */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Alert Frequency</Label>
              <div className="text-sm text-muted-foreground mb-3">
                Choose how frequently you want to receive security alerts.
              </div>
              <RadioGroup 
                value={alertFrequency} 
                onValueChange={setAlertFrequency}
                className="space-y-3"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="immediate" id="frequency-immediate" />
                  <Label 
                    htmlFor="frequency-immediate" 
                    className="flex-1 cursor-pointer font-normal"
                  >
                    <div className="font-medium">Immediate</div>
                    <div className="text-sm text-muted-foreground">
                      Receive alerts as soon as they are detected
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="daily" id="frequency-daily" />
                  <Label 
                    htmlFor="frequency-daily" 
                    className="flex-1 cursor-pointer font-normal"
                  >
                    <div className="font-medium">Daily Digest</div>
                    <div className="text-sm text-muted-foreground">
                      Receive a summary of all alerts once per day
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="weekly" id="frequency-weekly" />
                  <Label 
                    htmlFor="frequency-weekly" 
                    className="flex-1 cursor-pointer font-normal"
                  >
                    <div className="font-medium">Weekly Summary</div>
                    <div className="text-sm text-muted-foreground">
                      Receive a weekly summary of all security alerts
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Alert Type */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-semibold">Alert Types</Label>
              <div className="text-sm text-muted-foreground mb-3">
                Choose which types of security alerts you want to receive.
              </div>
              <RadioGroup 
                value={alertType} 
                onValueChange={setAlertType}
                className="space-y-3"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="type-all" />
                  <Label 
                    htmlFor="type-all" 
                    className="flex-1 cursor-pointer font-normal"
                  >
                    <div className="font-medium">All Alerts</div>
                    <div className="text-sm text-muted-foreground">
                      Receive all types of security alerts
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="critical" id="type-critical" />
                  <Label 
                    htmlFor="type-critical" 
                    className="flex-1 cursor-pointer font-normal"
                  >
                    <div className="font-medium">Critical Only</div>
                    <div className="text-sm text-muted-foreground">
                      Only receive critical security alerts
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="changes" id="type-changes" />
                  <Label 
                    htmlFor="type-changes" 
                    className="flex-1 cursor-pointer font-normal"
                  >
                    <div className="font-medium">Changes Only</div>
                    <div className="text-sm text-muted-foreground">
                      Only receive alerts for significant changes
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="pt-4 border-t">
              <Button 
                onClick={handleSaveSettings}
                disabled={updateSettingsMutation.isPending}
                className="w-full sm:w-auto"
              >
                {updateSettingsMutation.isPending ? "Saving..." : "Save Alert Preferences"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>
              Manage your account security settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium">Multi-Factor Authentication (MFA)</h3>
                <p className="text-sm text-muted-foreground">
                  Add an extra layer of security with Google Authenticator
                </p>
              </div>
              <Link href="/mfa-setup">
                <Button variant="outline">
                  Manage MFA
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Data Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Data Management
            </CardTitle>
            <CardDescription>
              Manage your scan data and findings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-destructive mb-1">Danger Zone</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Permanently delete all scans, findings, alerts, and related data. This action cannot be undone.
                    </p>
                    <AlertDialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" disabled={isCleaningUp}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          {isCleaningUp ? "Cleaning Up..." : "Delete All Scans & Findings"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription className="space-y-2">
                            <p>
                              This will permanently delete <strong>all</strong> of your:
                            </p>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                              <li>Scan history and results</li>
                              <li>Security alerts and findings</li>
                              <li>Request parameters</li>
                              <li>JavaScript scan data</li>
                              <li>Domain and application records</li>
                              <li>Network connection data</li>
                            </ul>
                            <p className="mt-3 font-semibold text-destructive">
                              This action cannot be undone. Are you sure you want to continue?
                            </p>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={isCleaningUp}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleCleanup}
                            disabled={isCleaningUp}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {isCleaningUp ? "Deleting..." : "Yes, Delete Everything"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}